import { FinancedAmountStore } from '@servicetitan/financing-plans-shared';
import {
    SetupDataStore,
    useSetSetupData,
    useSyncProviderToFinancingApi,
} from '@servicetitan/financing-workflow-shared';
import { LoadingScreen, useLaunchDarklyReady } from '@servicetitan/financing-utils-shared';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useCustomerData } from './useCustomerData';
import { useTechnicianData } from '@servicetitan/financing-application-shared';
import { LoanApplicationSubmitSource2 } from '@servicetitan/financing-api-client';
import { CustomerDataLoadingStore } from './CustomerDataLoadingStore';

interface SetSetupDataProps {
    buId: string;
    tenantId: string;
    technicianId: string;
    jobId: string;
    customerId: string;
    estimateAmount: number;
    serviceLocationState: string;
    provider?: string;
    useInAppBrowser?: boolean;
}

// This component exists to set some data the rest of the application needs to function properly.
// It also blocks rendering of the children until the data is set, mostly due to the provider
// being retrieved from the API.
export const SetSetupData = ({
    children,
    tenantId,
    buId,
    technicianId,
    jobId,
    customerId,
    estimateAmount,
    serviceLocationState,
    provider,
    useInAppBrowser,
}: PropsWithChildren<SetSetupDataProps>) => {
    const { setFinancedAmount } = FinancedAmountStore.useStore();
    const [showLoader, setShowLoader] = useState(false);
    const isLdReady = useLaunchDarklyReady();

    useEffect(() => {
        setFinancedAmount({ saleAmount: estimateAmount, downPayment: 0 });
    }, [estimateAmount, setFinancedAmount]);

    useSetSetupData({
        role: 'technician',
        provider,
        tenantId,
        buId,
        technicianId,
        jobId,
        customerId,
        state: serviceLocationState,
        source: LoanApplicationSubmitSource2.Mobile,
        useInAppBrowser,
    });

    useSyncProviderToFinancingApi();

    const customerData = useCustomerData({ jobId, tenantId, technicianId });
    useTechnicianData({ technicianId, tenantId });

    const { setCustomerDataLoading } = CustomerDataLoadingStore.useStore();

    useEffect(() => {
        setCustomerDataLoading(customerData.isLoading);
    }, [customerData.isLoading, setCustomerDataLoading]);

    useEffect(() => {
        // Show loader after 750ms to avoid flickering
        setTimeout(() => {
            setShowLoader(true);
        }, 750);
    }, [setShowLoader]);

    const { setupData } = SetupDataStore.useStore();

    if (!setupData?.provider || !isLdReady) {
        return showLoader ? <LoadingScreen message="Loading..." /> : null;
    }

    return children;
};
